import { useRef, useState, useEffect } from 'react';
import type { CSSProperties, JSX, PointerEvent as ReactPointerEvent } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface RankingFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (ordered: string[]) => void;
}

// Ranking field — supports both drag-and-drop (grip handle, mouse + touch via
// Pointer Events) and tap-based up/down arrows for accessibility. The drag
// is implemented inline (no @dnd-kit dependency) to keep the SDK bundle
// small while matching the main app's UX.
export function RankingField({ palette, question, value, onChange }: RankingFieldProps): JSX.Element {
  const options = question.settings?.options ?? [];
  const defaultOrder = options.map((option) => option.value);
  const currentOrder = resolveOrder(value, defaultOrder);
  const labels = new Map(options.map((option) => [option.value, option.label]));

  const listRef = useRef<HTMLOListElement | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const dragState = useRef<{ startY: number; offsetY: number; pointerId: number } | null>(null);

  const move = (from: number, to: number): void => {
    if (to < 0 || to >= currentOrder.length) return;
    const next = [...currentOrder];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    onChange(next);
  };

  const handlePointerDown = (e: ReactPointerEvent<HTMLButtonElement>, optionValue: string): void => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    const list = listRef.current;
    if (!list) return;
    const row = (e.currentTarget.closest('li') as HTMLLIElement | null);
    if (!row) return;
    const rowRect = row.getBoundingClientRect();
    dragState.current = { startY: e.clientY, offsetY: e.clientY - rowRect.top, pointerId: e.pointerId };
    setDraggingId(optionValue);
    setHoverIndex(currentOrder.indexOf(optionValue));
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };

  // Move + drop are bound to window so the gesture stays alive even when the
  // pointer leaves the source row.
  useEffect(() => {
    if (!draggingId) return;
    const handleMove = (event: PointerEvent): void => {
      const list = listRef.current;
      if (!list || !dragState.current) return;
      if (event.pointerId !== dragState.current.pointerId) return;
      event.preventDefault();
      const rows = Array.from(list.children) as HTMLElement[];
      let newIndex = currentOrder.indexOf(draggingId);
      for (let i = 0; i < rows.length; i += 1) {
        const r = rows[i];
        if (!r) continue;
        const rect = r.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;
        if (event.clientY < midpoint) {
          newIndex = i;
          break;
        }
        newIndex = i;
      }
      setHoverIndex(newIndex);
    };
    const handleUp = (event: PointerEvent): void => {
      if (!dragState.current || event.pointerId !== dragState.current.pointerId) return;
      const from = currentOrder.indexOf(draggingId);
      const to = hoverIndex ?? from;
      if (from !== to && from >= 0 && to >= 0) {
        const next = [...currentOrder];
        const [moved] = next.splice(from, 1);
        if (moved) {
          next.splice(to, 0, moved);
          onChange(next);
        }
      }
      dragState.current = null;
      setDraggingId(null);
      setHoverIndex(null);
    };
    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [draggingId, currentOrder, hoverIndex, onChange]);

  return (
    <ol ref={listRef} style={listStyle()}>
      {currentOrder.map((optionValue, index) => {
        const isDragging = draggingId === optionValue;
        const showDropLine = draggingId && hoverIndex === index && !isDragging;
        return (
          <li
            key={optionValue}
            style={{
              ...rowStyle(palette),
              ...(isDragging ? draggingRowStyle(palette) : {}),
              ...(showDropLine ? dropTargetStyle(palette) : {}),
              touchAction: 'none',
            }}
          >
            <button
              type="button"
              onPointerDown={(e) => handlePointerDown(e, optionValue)}
              aria-label={`Drag ${labels.get(optionValue) ?? optionValue} to reorder`}
              className="chirp-btn"
              style={dragHandleStyle(palette, isDragging)}
            >
              <GripIcon color={isDragging ? palette.primary : hexWithAlpha(palette.textMuted, 0.6)} />
            </button>
            <div style={moveColumnStyle()}>
              <MoveButton
                palette={palette}
                disabled={index === 0}
                onClick={() => move(index, index - 1)}
                direction="up"
              />
              <MoveButton
                palette={palette}
                disabled={index === currentOrder.length - 1}
                onClick={() => move(index, index + 1)}
                direction="down"
              />
            </div>
            <span style={badgeStyle(palette)}>{index + 1}</span>
            <span style={labelStyle(palette)}>{labels.get(optionValue) ?? optionValue}</span>
          </li>
        );
      })}
    </ol>
  );
}

function MoveButton({
  palette,
  disabled,
  onClick,
  direction,
}: {
  palette: Palette;
  disabled: boolean;
  onClick: () => void;
  direction: 'up' | 'down';
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'up' ? 'Move up' : 'Move down'}
      className="chirp-btn"
      style={{
        width: 22,
        height: 18,
        border: 'none',
        background: 'transparent',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.3 : 0.7,
        color: palette.textMuted,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ChevronIcon direction={direction} />
    </button>
  );
}

function ChevronIcon({ direction }: { direction: 'up' | 'down' }): JSX.Element {
  return (
    <svg width="12" height="8" viewBox="0 0 12 8" fill="currentColor" aria-hidden="true">
      {direction === 'up' ? (
        <path d="M6 0L12 8H0L6 0Z" />
      ) : (
        <path d="M6 8L0 0H12L6 8Z" />
      )}
    </svg>
  );
}

function GripIcon({ color }: { color: string }): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="9" cy="6" r="1.6" fill={color} />
      <circle cx="9" cy="12" r="1.6" fill={color} />
      <circle cx="9" cy="18" r="1.6" fill={color} />
      <circle cx="15" cy="6" r="1.6" fill={color} />
      <circle cx="15" cy="12" r="1.6" fill={color} />
      <circle cx="15" cy="18" r="1.6" fill={color} />
    </svg>
  );
}

function resolveOrder(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback;
  const list = (value as unknown[]).filter((entry): entry is string => typeof entry === 'string');
  if (list.length !== fallback.length) return fallback;
  return list;
}

function listStyle(): CSSProperties {
  return { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 };
}

function rowStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    border: `1px solid ${palette.border}`,
    borderRadius: 10,
    background: palette.card,
    transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
    userSelect: 'none',
  };
}

function draggingRowStyle(palette: Palette): CSSProperties {
  return {
    borderColor: palette.primary,
    boxShadow: `0 6px 18px -8px ${hexWithAlpha(palette.primary, 0.4)}`,
    opacity: 0.85,
  };
}

function dropTargetStyle(palette: Palette): CSSProperties {
  return {
    borderTop: `2px solid ${palette.primary}`,
  };
}

function dragHandleStyle(palette: Palette, active: boolean): CSSProperties {
  return {
    width: 22,
    height: 28,
    border: 'none',
    background: 'transparent',
    cursor: active ? 'grabbing' : 'grab',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: palette.textMuted,
    touchAction: 'none',
  };
}

function moveColumnStyle(): CSSProperties {
  return { display: 'flex', flexDirection: 'column', gap: 2 };
}

function badgeStyle(palette: Palette): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: 6,
    background: palette.primary,
    color: palette.onPrimary,
    fontSize: 12,
    fontWeight: 600,
    flexShrink: 0,
  };
}

function labelStyle(palette: Palette): CSSProperties {
  return { flex: 1, fontSize: 14, color: palette.text, wordBreak: 'break-word' };
}
